import { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

// ─── Flag images via flagcdn.com (free, reliable CDN) ─────────────────
const LANGUAGES = [
  {
    code: "de",
    label: "Deutsch",
    flag: "https://flagcdn.com/w40/de.png",
    flag2x: "https://flagcdn.com/w80/de.png",
  },
  {
    code: "en",
    label: "English",
    flag: "https://flagcdn.com/w40/gb.png",
    flag2x: "https://flagcdn.com/w80/gb.png",
  },
  {
    code: "sq",
    label: "Shqip",
    flag: "https://flagcdn.com/w40/al.png",
    flag2x: "https://flagcdn.com/w80/al.png",
  },
];

// ─── Styled ────────────────────────────────────────────────────────────
const Wrapper = styled.div`
  position: relative;
  z-index: 500;
  user-select: none;
`;

const Trigger = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  background: none;
  border: none;
  padding: 0.3rem 0;
  cursor: pointer;
  outline: none;
`;

const FlagImg = styled.img`
  width: 24px;
  height: 16px;
  object-fit: cover;
  border-radius: 2px;
  display: block;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
`;

const LangLabel = styled(motion.span)`
  font-family: var(--font-sans);
  font-size: 0.58rem;
  font-weight: 300;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: white;
  transition: color 0.3s;
`;

const Chevron = styled(motion.span)`
  display: flex;
  align-items: center;
  color: ${({ $dark }) =>
    $dark ? "rgba(247,243,238,0.3)" : "var(--warm-tan)"};
`;

// ── Dropdown ───────────────────────────────────────────────────────────
const Dropdown = styled(motion.div)`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  background: var(--cream);
  border: 1px solid rgba(122, 92, 71, 0.1);
  min-width: 148px;
  overflow: hidden;
  box-shadow:
    0 4px 16px rgba(46, 31, 20, 0.08),
    0 16px 48px rgba(46, 31, 20, 0.1);
`;

const DropItem = styled(motion.button)`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.8rem 1rem;
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(122, 92, 71, 0.07);
  cursor: pointer;
  text-align: left;
  transition: background 0.2s;
  -webkit-tap-highlight-color: transparent;
  color: white;

  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: rgba(184, 115, 85, 0.05);
  }
`;

const DropFlagImg = styled.img`
  width: 22px;
  height: 15px;
  object-fit: cover;
  border-radius: 2px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
`;

const DropLabel = styled.span`
  font-family: var(--font-sans);
  font-size: 0.65rem;
  font-weight: 300;
  letter-spacing: 0.1em;
  color: white;
  flex: 1;
  transition: color 0.2s;
`;

const ActiveMark = styled.span`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--terracotta);
  flex-shrink: 0;
`;

// ─── Component ─────────────────────────────────────────────────────────
export default function LanguageSwitcher({ dark = false }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const current =
    LANGUAGES.find((l) => l.code === i18n.language) ?? LANGUAGES[0];

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const switchLang = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <Wrapper ref={wrapRef}>
      {/* ── Trigger ── */}
      <Trigger
        onClick={() => setOpen((o) => !o)}
        whileTap={{ scale: 0.94 }}
        aria-label="Change language"
      >
        <FlagImg
          src={current.flag}
          srcSet={`${current.flag} 1x, ${current.flag2x} 2x`}
          alt={current.label}
        />
        <LangLabel $dark={dark}>{current.code}</LangLabel>
        <Chevron
          $dark={dark}
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
        >
          <svg width="8" height="5" viewBox="0 0 8 5" fill="none">
            <path
              d="M1 1l3 3 3-3"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Chevron>
      </Trigger>

      {/* ── Dropdown ── */}
      <AnimatePresence>
        {open && (
          <Dropdown
            initial={{ opacity: 0, y: -8, scaleY: 0.9 }}
            animate={{ opacity: 1, y: 0, scaleY: 1 }}
            exit={{ opacity: 0, y: -4, scaleY: 0.94 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            style={{ transformOrigin: "top right" }}
          >
            {LANGUAGES.map((lang, i) => (
              <DropItem
                key={lang.code}
                onClick={() => switchLang(lang.code)}
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05, duration: 0.2 }}
              >
                <DropFlagImg
                  src={lang.flag}
                  srcSet={`${lang.flag} 1x, ${lang.flag2x} 2x`}
                  alt={lang.label}
                />
                <DropLabel>{lang.label}</DropLabel>
                {lang.code === i18n.language && <ActiveMark />}
              </DropItem>
            ))}
          </Dropdown>
        )}
      </AnimatePresence>
    </Wrapper>
  );
}
